import type {
	TaskTemplatePlaceholderInput,
	TaskTemplatePlaceholderType,
} from "@/common/artemis-sdk/graphql";

/**
 * Placeholders: named values a template references as `{{key}}` tokens in its text
 * fields and asks for when it is used. Gallery templates declare them in their YAML
 * (`placeholders.variables[]`); workspace task templates store them on the template.
 * Both share the shape, the input controls and the substitution defined here.
 */

/** The kind of value a placeholder asks for. `aws-region`/`aws-account-id` are native
 * types: the app renders a real picker/validated input for them instead of a text box. */
export type PlaceholderValueType =
	| "string"
	| "number"
	| "boolean"
	| "aws-region"
	| "aws-account-id";

/** A value filled in for a placeholder. */
export type PlaceholderValue = string | number | boolean;

/** One declared placeholder, as both gallery and workspace templates describe it. */
export interface PlaceholderField {
	key: string;
	label: string;
	description?: string;
	type: PlaceholderValueType;
	default?: PlaceholderValue;
	required?: boolean;
}

/** Every placeholder type, in the order the editor offers them. */
export const PLACEHOLDER_TYPES: {
	value: PlaceholderValueType;
	label: string;
	description: string;
}[] = [
	{ value: "string", label: "Text", description: "Free text." },
	{ value: "number", label: "Number", description: "A number." },
	{
		value: "boolean",
		label: "Yes / No",
		description: "A toggle, inserted as true or false.",
	},
	{
		value: "aws-region",
		label: "AWS region",
		description: "Picked from the AWS region list, inserted as e.g. us-east-1.",
	},
	{
		value: "aws-account-id",
		label: "AWS account ID",
		description: "A validated 12-digit AWS account ID.",
	},
];

const TO_API_TYPE: Record<PlaceholderValueType, TaskTemplatePlaceholderType> = {
	string: "STRING",
	number: "NUMBER",
	boolean: "BOOLEAN",
	"aws-region": "AWS_REGION",
	"aws-account-id": "AWS_ACCOUNT_ID",
};

const FROM_API_TYPE = Object.fromEntries(
	Object.entries(TO_API_TYPE).map(([k, v]) => [v, k]),
) as Record<TaskTemplatePlaceholderType, PlaceholderValueType>;

/** Matches one `{{key}}` token; the key is letters, digits and underscores. */
const TOKEN_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

/** Splits `text` into plain runs and `{{key}}` tokens, in order — for highlighting. */
export function tokenizePlaceholders(
	text: string,
): { text: string; key?: string }[] {
	const parts: { text: string; key?: string }[] = [];
	let last = 0;
	for (const match of text.matchAll(TOKEN_PATTERN)) {
		const index = match.index ?? 0;
		if (index > last) parts.push({ text: text.slice(last, index) });
		parts.push({ text: match[0], key: match[1] });
		last = index + match[0].length;
	}
	if (last < text.length) parts.push({ text: text.slice(last) });
	return parts;
}

/** Every distinct `{{key}}` referenced across `texts`, in order of first appearance. */
export function findPlaceholderKeys(
	...texts: (string | null | undefined)[]
): string[] {
	const keys = new Set<string>();
	for (const text of texts) {
		for (const match of (text ?? "").matchAll(TOKEN_PATTERN)) {
			keys.add(match[1]);
		}
	}
	return Array.from(keys);
}

/** Substitutes `{{key}}` tokens in `text` with the matching value from `values`. */
export function substituteVariables(
	text: string,
	values: Record<string, PlaceholderValue>,
): string {
	return text.replace(TOKEN_PATTERN, (match, key: string) =>
		key in values ? String(values[key]) : match,
	);
}

/**
 * The substitution values for `fields`, read from a form via `read(key)`. A blank
 * optional value becomes an empty string, so its token is removed rather than left
 * in the text (or replaced with "undefined").
 */
export function collectPlaceholderValues(
	fields: PlaceholderField[],
	read: (key: string) => PlaceholderValue | null | undefined,
): Record<string, PlaceholderValue> {
	const values: Record<string, PlaceholderValue> = {};
	for (const field of fields) {
		values[field.key] = read(field.key) ?? "";
	}
	return values;
}

/** `idle_days` → `Idle days`: the label a newly detected placeholder starts with. */
export function humanizeKey(key: string): string {
	const words = key.replace(/_+/g, " ").trim();
	return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Re-aligns a template's declared placeholders with the keys its text references:
 * one entry per key, in `keys` order. An existing declaration keeps its settings;
 * a key seen before (in `remembered`, e.g. deleted then retyped) gets its old ones
 * back; a brand-new key starts as a required text value labelled from the key.
 */
export function syncPlaceholders(
	current: PlaceholderField[],
	keys: string[],
	remembered: ReadonlyMap<string, PlaceholderField> = new Map(),
): PlaceholderField[] {
	const byKey = new Map(current.map((p) => [p.key, p]));
	return keys.map(
		(key) =>
			byKey.get(key) ??
			remembered.get(key) ?? {
				key,
				label: humanizeKey(key),
				type: "string",
				required: true,
			},
	);
}

/** A stored task-template placeholder as the form and controls work with it. */
export function fromTaskTemplatePlaceholder(p: {
	key: string;
	label: string;
	description?: string | null;
	type: TaskTemplatePlaceholderType;
	required: boolean;
	defaultValue?: string | null;
}): PlaceholderField {
	const type = FROM_API_TYPE[p.type] ?? "string";
	const raw = p.defaultValue ?? undefined;
	const parsed =
		raw === undefined
			? undefined
			: type === "number"
				? Number(raw)
				: type === "boolean"
					? raw === "true"
					: raw;
	return {
		key: p.key,
		label: p.label,
		description: p.description ?? undefined,
		type,
		required: p.required,
		default: parsed,
	};
}

/** A form placeholder as the create/update task-template mutations take it. */
export function toTaskTemplatePlaceholderInput(
	p: PlaceholderField,
): TaskTemplatePlaceholderInput {
	const hasDefault =
		p.default !== undefined && p.default !== null && p.default !== "";
	return {
		key: p.key,
		label: p.label,
		description: p.description || undefined,
		type: TO_API_TYPE[p.type],
		required: p.required !== false,
		defaultValue: hasDefault ? String(p.default) : undefined,
	};
}
